// src/components/MapComponent.tsx
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { usePopup } from "../hooks/usePopup";
import {
  MapContainer,
  TileLayer,
  Circle,
  Popup,
  useMapEvents,
  Marker,
  Tooltip,
} from "react-leaflet";
import L, { Map as LeafletMap } from "leaflet";
import { useNavigate } from "react-router-dom"; // NEW: Import for navigation
import { NoiseLocation, SearchResult, NoiseCluster, NoiseAreaStatus } from "../types/mapTypes";
import { mapConfig, tileLayerConfig, noiseColors } from "../config/mapConfig";
import { mapService } from "../services/mapService";
import {
  generateNoiseArea,
  computeNoiseAreaStatus,
  getCircleStyleByStatus,
  getStatusTooltip,
  getNoiseColor,
  getNoiseRadius,
  getNoiseOpacity,
  formatNoiseLevel,
  calculateDistance,
  formatCoordinates,
  formatRadius,
  getNoiseDescription,
} from "../utils/mapUtils";
import MapControls from "./MapControls";
import MapPopup from "./MapPopup";
import { ChatBot } from "./Chat";
import AreaFilter, { AreaFilters } from "./AreaFilter";
import MapTutorial from "./MapTutorial";
import styles from "../styles/MapComponent.module.css";
import { useAuth } from "../contexts/AuthContext";
import { getUserProfile } from "../services/profileService";

import "leaflet/dist/leaflet.css";
import { appConfig, logger } from "../config/appConfig";
import { deriveFinalCategory } from "../services/map.transformers";
import {
  translateNoiseSource,
  translateHealthImpact,
} from "../utils/translationUtils";

// PERBAIKAN: Fix untuk ikon default Leaflet yang sering rusak di React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// BARU: Custom icon untuk lokasi pengguna
const userLocationIcon = L.divIcon({
  className: "user-location-marker",
  html: `
    <div style="
      position: relative;
      width: 20px;
      height: 20px;
    ">
      <div style="
        width: 20px;
        height: 20px;
        background: #007AFF;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,122,255,0.3);
        animation: pulse-user 2s infinite;
      "></div>
      <div style="
        position: absolute;
        top: -10px;
        left: -10px;
        width: 40px;
        height: 40px;
        background: rgba(0,122,255,0.2);
        border-radius: 50%;
        animation: pulse-ring 2s infinite;
      "></div>
    </div>
    <style>
      @keyframes pulse-user {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); }
      }
      @keyframes pulse-ring {
        0% { transform: scale(0.8); opacity: 1; }
        100% { transform: scale(2); opacity: 0; }
      }
    </style>
  `,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// BARU: Custom icon untuk search location
const searchLocationIcon = L.divIcon({
  className: "search-location-marker",
  html: `
    <div style="
      position: relative;
      width: 30px;
      height: 30px;
    ">
      <div style="
        width: 30px;
        height: 30px;
        background: #ff4444;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 3px 10px rgba(255,68,68,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 16px;
        font-weight: bold;
      ">📍</div>
      <div style="
        position: absolute;
        top: -5px;
        left: -5px;
        width: 40px;
        height: 40px;
        background: rgba(255,68,68,0.2);
        border-radius: 50%;
        animation: search-pulse 2s infinite;
      "></div>
    </div>
    <style>
      @keyframes search-pulse {
        0% { transform: scale(1); opacity: 0.8; }
        50% { transform: scale(1.2); opacity: 0.4; }
        100% { transform: scale(1.5); opacity: 0; }
      }
    </style>
  `,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

interface MapComponentProps {
  className?: string;
}

const MapComponent: React.FC<MapComponentProps> = ({ className }) => {
  const navigate = useNavigate(); // NEW: Hook for navigation
  const { isAuthenticated } = useAuth();
  const [noiseLocations, setNoiseLocations] = useState<NoiseLocation[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const {
    // popupState,  // removed unused
    // hidePopup,   // removed unused
    showSuccess,
    showError,
    // showWarning, // removed unused
    showConfirm,
    showLogin,
    PopupComponent,
  } = usePopup();
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isAddingNoise, setIsAddingNoise] = useState<boolean>(false);
  const [showNoiseForm, setShowNoiseForm] = useState<boolean>(false);
  const [showLegend, setShowLegend] = useState<boolean>(true);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [formDescription, setFormDescription] = useState<string>(""); // State untuk deskripsi
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [selectedPosition, setSelectedPosition] = useState<
    [number, number] | null
  >(null);
  const [searchMarker, setSearchMarker] = useState<[number, number] | null>(
    null
  );
  const [userLocation, setUserLocation] = useState<[number, number] | null>(
    null
  );
  // Cluster state
  const [noiseClusters, setNoiseClusters] = useState<NoiseCluster[]>([]);
  const [clustersLoading, setClustersLoading] = useState<boolean>(false);
  const [clustersError, setClustersError] = useState<string>("");
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  const [clusterAddresses, setClusterAddresses] = useState<
    Record<string, string>
  >({});

  // Auto-fill address for clusters based on their centroid
  useEffect(() => {
    const fetchMissingAddresses = async () => {
      try {
        const tasks = noiseClusters
          .filter((c) => !(c.id in clusterAddresses))
          .map(async (c) => {
            const [clat, clon] = c.center;
            try {
              const addr = await mapService.reverseGeocode(clat, clon);
              return {
                id: c.id,
                address: addr || `(${clat.toFixed(6)}, ${clon.toFixed(6)})`,
              };
            } catch {
              return {
                id: c.id,
                address: `(${clat.toFixed(6)}, ${clon.toFixed(6)})`,
              };
            }
          });
        if (tasks.length > 0) {
          const results = await Promise.all(tasks);
          setClusterAddresses((prev) => {
            const updated: Record<string, string> = { ...prev };
            for (const r of results) {
              updated[r.id] = r.address;
            }
            return updated;
          });
        }
      } catch (e) {
        logger.warn?.("Gagal memuat alamat cluster", e);
      }
    };
    fetchMissingAddresses();
  }, [noiseClusters]);

  // NEW: Cek apakah user saat ini berkontribusi pada cluster (berdasarkan username)
  const hasCurrentUserContribution = useCallback(
    (cluster: NoiseCluster) => {
      const uname = currentUsername || localStorage.getItem("username");
      if (!uname) return false;
      return (
        Array.isArray(cluster.addedByUsernames) &&
        cluster.addedByUsernames.includes(uname)
      );
    },
    [currentUsername]
  );

  // NEW: Muat username saat user login (fallback ke localStorage)
  useEffect(() => {
    const loadUsername = async () => {
      try {
        if (appConfig.backendEnabled && isAuthenticated) {
          const profile = await getUserProfile();
          if (profile?.username) {
            setCurrentUsername(profile.username);
            return;
          }
        }
        const local = localStorage.getItem("username");
        if (local) setCurrentUsername(local);
      } catch (e) {
        const local = localStorage.getItem("username");
        if (local) setCurrentUsername(local);
        logger.warn?.("Gagal memuat username", e);
      }
    };
    loadUsername();
  }, [isAuthenticated]);

  // Hanya sembunyikan popup single-report jika lokasinya tergabung pada cluster (>=2 laporan)
  // Spatial threshold diselaraskan dengan fungsi SQL: ST_DWithin(..., 30m) + toleransi kecil
  const CLUSTER_DISTANCE_THRESHOLD = 35; // meters
  const isLocationCoveredByAnyCluster = useCallback(
    (location: NoiseLocation) => {
      if (!appConfig.backendEnabled || noiseClusters.length === 0) return false;
      for (const c of noiseClusters) {
        if ((c.reportCount ?? 0) <= 1) continue; // cluster artinya minimal 2 laporan
        const d = calculateDistance(
          location.coordinates[0],
          location.coordinates[1],
          c.center[0],
          c.center[1]
        );
        // PERUBAHAN: Abaikan jendela waktu; cukup gunakan kedekatan spasial
        if (d <= CLUSTER_DISTANCE_THRESHOLD) {
          return true;
        }
      }
      return false;
    },
    [noiseClusters]
  );
  const [isTrackingUser, setIsTrackingUser] = useState<boolean>(false);
  const [searchLocationMarker, setSearchLocationMarker] = useState<{
    position: [number, number];
    name: string;
    address: string;
  } | null>(null);

  // NEW: State for the address input in the form
  const [formAddress, setFormAddress] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [activeFilters, setActiveFilters] = useState<AreaFilters>({});
  const mapRef = useRef<LeafletMap | null>(null);
  const reanalysisFileInputRef = useRef<HTMLInputElement | null>(null);
  const [locationToReanalyze, setLocationToReanalyze] =
    useState<NoiseLocation | null>(null);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const watchId = useRef<number | null>(null);

  // Tutorial state
  const [showTutorial, setShowTutorial] = useState<boolean>(false);

  useEffect(() => {
    loadNoiseLocations();
    handleLocateUser();
    // Load clusters (backend only)
    loadNoiseClusters();

    // Check if this is the first visit to show tutorial
    const hasSeenTutorial = localStorage.getItem("hasSeenMapTutorial");
    if (!hasSeenTutorial) {
      // Delay tutorial to ensure map is loaded
      setTimeout(() => {
        setShowTutorial(true);
      }, 1000);
    }

    // UPDATED: Check for shared data from either flow
    const sharedData = mapService.getSharedNoiseData();
    if (sharedData) {
      processSharedData(sharedData);
    }

    return () => {
      if (watchId.current !== null) {
        navigator.geolocation.clearWatch(watchId.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tutup popup ketika masuk mode tambah area untuk mencegah konflik klik
  useEffect(() => {
    if (isAddingNoise) {
      try {
        mapRef.current?.closePopup();
      } catch (e) {
        // no-op
      }
    }
  }, [isAddingNoise]);

  // Utility: process shared noise data passed from other flows
  const processSharedData = async (data: {
    analysis: any;
    position?: [number, number];
    address?: string;
  }) => {
    try {
      if (data.position) {
        setSelectedPosition(data.position);
        setShowNoiseForm(true);
      }
      if (data.address && data.address.trim()) {
        setFormAddress(data.address);
      } else if (data.position) {
        // Jika alamat tidak tersedia, lakukan reverse geocoding menggunakan Nominatim
        try {
          const addr = await mapService.reverseGeocode(
            data.position[0],
            data.position[1]
          );
          if (addr) {
            setFormAddress(addr);
          } else {
            // Fallback ke koordinat bila gagal
            const lat = Number(data.position[0]).toFixed(6);
            const lon = Number(data.position[1]).toFixed(6);
            setFormAddress(`(${lat}, ${lon})`);
          }
        } catch (err) {
          const lat = Number(data.position[0]).toFixed(6);
          const lon = Number(data.position[1]).toFixed(6);
          setFormAddress(`(${lat}, ${lon})`);
        }
      }
      if (data.analysis) {
        logger.info("Received shared analysis data", data.analysis);
      }
    } catch (e) {
      logger.error("Failed processing shared data:", e);
      showError("Error", "Gagal memproses data berbagi");
    }
  };

  // Start tracking user when isTrackingUser is enabled
  useEffect(() => {
    if (isTrackingUser && navigator.geolocation) {
      watchId.current = navigator.geolocation.watchPosition(
        (position) => {
          const newLocation: [number, number] = [
            position.coords.latitude,
            position.coords.longitude,
          ];
          setUserLocation(newLocation);
        },
        (error) => {
          logger.warn("Tracking location error:", error);
          setIsTrackingUser(false);
        }
      );
    }
    return () => {
      if (watchId.current !== null) {
        navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
      }
    };
  }, [isTrackingUser]);

  const zoomToLocation = (
    coordinates: [number, number],
    zoomLevel: number = 15,
    showMarker: boolean = true
  ) => {
    if (mapRef.current) {
      mapRef.current.flyTo(coordinates, zoomLevel, {
        animate: true,
        duration: 1.5, // Durasi animasi dalam detik
        easeLinearity: 0.25,
      });
      if (showMarker && !userLocation) {
        setSearchMarker(coordinates);
        setTimeout(() => setSearchMarker(null), 5000);
      }
    }
  };

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length > 2) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = await mapService.searchLocations(searchQuery);
          setSearchResults(results);
        } catch (error) {
          setError("Pencarian gagal");
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300); // Dipercepat dari 500ms ke 300ms untuk responsif
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const loadNoiseLocations = async () => {
    try {
      setLoading(true);
      const locations = await mapService.getNoiseLocations();
      setNoiseLocations(locations);
    } catch (error) {
      logger.error("Error loading noise locations:", error);
      setError("Gagal memuat data area noise");
    } finally {
      setLoading(false);
    }
  };

  // Loader untuk data cluster dari backend
  const loadNoiseClusters = async () => {
    if (!appConfig.backendEnabled) {
      setNoiseClusters([]);
      setClustersError("");
      return;
    }
    try {
      setClustersLoading(true);
      const clusters = await mapService.getNoiseClusters();
      setNoiseClusters(clusters);
      setClustersError("");
    } catch (err) {
      logger.error("Error loading noise clusters:", err);
      setClustersError("Gagal memuat cluster kebisingan");
    } finally {
      setClustersLoading(false);
    }
  };

  const handleMapClick = async (e: any) => {
    if (isAddingNoise) {
      const lat = e.latlng.lat;
      const lon = e.latlng.lng;
      setSelectedPosition([lat, lon]);
      setShowNoiseForm(true);
      // Isikan alamat otomatis menggunakan Nominatim
      try {
        const addr = await mapService.reverseGeocode(lat, lon);
        if (addr) {
          setFormAddress(addr);
        } else {
          setFormAddress(`(${lat.toFixed(6)}, ${lon.toFixed(6)})`);
        }
      } catch (err) {
        setFormAddress(`(${lat.toFixed(6)}, ${lon.toFixed(6)})`);
      }
    }
    setSearchMarker(null);
    if (searchLocationMarker) {
      setSearchLocationMarker(null);
    }
  };
  const handleAddNoiseArea = async () => {
    if (!appConfig.backendEnabled) {
      setError("Fitur analisis area tidak tersedia saat backend dinonaktifkan");
      return;
    }

    // Removed legacy SessionManager check, use AuthContext instead
    if (!isAuthenticated) {
      showLogin(
        "Login Diperlukan",
        "Anda harus login untuk dapat menambahkan area analisis di peta.",
        () => navigate("/login")
      );
      return;
    }

    setIsAddingNoise(!isAddingNoise);
    // Reset form state when toggling the mode off
    if (isAddingNoise) {
      setShowNoiseForm(false);
      setSelectedPosition(null);
      setFormAddress("");
    }
  };

  // NEW: Handler for the "Analisis Suara" button
  const handleUploadAndAnalyze = async () => {
    if (!appConfig.backendEnabled) {
      setError(
        "Fitur analisis audio tidak tersedia saat backend dinonaktifkan"
      );
      return;
    }

    if (!selectedPosition) {
      setError("Lokasi di peta belum dipilih.");
      return;
    }

    if (!audioFile) {
      setError("Silakan pilih file audio untuk dianalisis.");
      return;
    }

    // VALIDASI TAMBAHAN: Periksa format dan ukuran file di frontend
    const allowedTypes = [
      "audio/wav",
      "audio/mp3",
      "audio/mpeg",
      "audio/mp4",
      "audio/m4a",
      "audio/ogg",
      "audio/webm",
      "audio/flac",
    ];
    if (!allowedTypes.includes(audioFile.type)) {
      setError(
        `Format file tidak didukung: ${audioFile.type}. Gunakan format WAV, MP3, M4A, OGG, WebM, atau FLAC.`
      );
      return;
    }

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (audioFile.size > maxSize) {
      setError(
        `Ukuran file terlalu besar (${(audioFile.size / 1024 / 1024).toFixed(
          1
        )}MB). Maksimal 50MB.`
      );
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      logger.info("🎵 Memulai analisis audio:", {
        fileName: audioFile.name,
        fileSize: `${(audioFile.size / 1024 / 1024).toFixed(2)}MB`,
        fileType: audioFile.type,
        position: selectedPosition,
        address: formAddress,
      });

      const newLocation = await mapService.analyzeAudioAndAddArea(
        audioFile,
        selectedPosition,
        formAddress,
        formDescription
      );

      if (newLocation) {
        logger.info("✅ Area berhasil ditambahkan:", newLocation);

        // Reload data dan zoom ke lokasi baru
        await loadNoiseLocations();
        await loadNoiseClusters();
        zoomToLocation(newLocation.coordinates, 17);

        // Reset form dan tutup panel
        setShowNoiseForm(false);
        setSelectedPosition(null);
        setFormAddress("");
        setFormDescription("");
        setAudioFile(null);
        setIsAddingNoise(false);

        // Tampilkan notifikasi sukses
        showSuccess(
          "Analisis Berhasil!",
          `Tingkat Kebisingan: ${newLocation.noiseLevel}\nSumber: ${newLocation.source}\nDampak Kesehatan: ${newLocation.healthImpact}`
        );
      } else {
        setError("Gagal membuat area kebisingan baru setelah analisis.");
      }
    } catch (err: any) {
      logger.error("❌ Error saat analisis:", err);

      // Tangani error koordinat sama
      if (err.message && err.message.includes("Koordinat sudah digunakan")) {
        setError(err.message);
        return;
      }

      // Tampilkan error yang lebih user-friendly
      let userMessage =
        err.message || "Terjadi kesalahan saat memproses file Anda.";

      // Tangani berbagai jenis error khusus
      if (userMessage.includes("400")) {
        userMessage =
          "Server menolak file audio. Pastikan format file benar dan tidak rusak.";
      } else if (userMessage.includes("401")) {
        userMessage = "Sesi Anda telah berakhir. Silakan login kembali.";
      } else if (userMessage.includes("413")) {
        userMessage = "Ukuran file terlalu besar. Maksimal 50MB.";
      } else if (userMessage.includes("429")) {
        userMessage =
          "Batas harian tercapai. Anda sudah menambahkan 5 titik dalam 24 jam terakhir.";
      } else if (userMessage.includes("500")) {
        userMessage =
          "Terjadi kesalahan pada server. Coba lagi dalam beberapa saat.";
      }

      setError(userMessage);
    } finally {
      setIsUploading(false);
    }
  };

  // TAMBAHAN: Handler untuk validasi file saat dipilih
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Reset error sebelumnya
      setError("");

      // Validasi format file
      const allowedTypes = [
        "audio/wav",
        "audio/mp3",
        "audio/mpeg",
        "audio/mp4",
        "audio/m4a",
        "audio/ogg",
        "audio/webm",
        "audio/flac",
      ];
      if (!allowedTypes.includes(file.type)) {
        setError(`Format file tidak didukung: ${file.type}`);
        e.target.value = ""; // Reset input
        return;
      }

      // Validasi ukuran file
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        setError(
          `File terlalu besar: ${(file.size / 1024 / 1024).toFixed(
            1
          )}MB. Maksimal 50MB.`
        );
        e.target.value = ""; // Reset input
        return;
      }

      logger.info("📁 File dipilih:", {
        name: file.name,
        size: file.size,
        type: file.type,
      });

      setAudioFile(file);
    }
  };
  const handleStartReanalysis = (location: NoiseLocation) => {
    if (!appConfig.backendEnabled) {
      setError(
        "Fitur analisis ulang tidak tersedia saat backend dinonaktifkan"
      );
      return;
    }
    setLocationToReanalyze(location); // Simpan info lokasi mana yang akan dianalisis
    reanalysisFileInputRef.current?.click(); // Buka jendela pilih file
  };

  // Fungsi ini berjalan setelah Anda memilih file audio
  const handleReanalysisFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!appConfig.backendEnabled) {
      setError(
        "Fitur analisis ulang tidak tersedia saat backend dinonaktifkan"
      );
      return;
    }

    const file = e.target.files?.[0];
    if (!file || !locationToReanalyze) return; // Batalkan jika tidak ada file

    setIsUploading(true);
    setError("");
    try {
      // Panggil service yang sudah Anda buat
      const updatedLocation = await mapService.updateNoiseLocationWithAudio(
        locationToReanalyze.id,
        file,
        {
          description: locationToReanalyze.description,
          address: locationToReanalyze.address,
        }
      );

      if (updatedLocation) {
        await loadNoiseLocations(); // Muat ulang data peta agar update
        showSuccess("Berhasil!", "Analisis ulang berhasil!");
      } else {
        setError("Gagal memperbarui setelah analisis ulang.");
      }
    } catch (err: any) {
      // Tangani error koordinat sama
      if (err.message && err.message.includes("Koordinat sudah digunakan")) {
        setError(err.message);
      } else {
        setError(err.message || "Terjadi kesalahan saat memproses file.");
      }
    } finally {
      setIsUploading(false);
      setLocationToReanalyze(null);
      if (e.target) e.target.value = ""; // Reset input
    }
  };
  const handleDeleteNoiseLocation = async (id: string) => {
    if (!appConfig.backendEnabled) {
      setError("Fitur hapus area tidak tersedia saat backend dinonaktifkan");
      return;
    }

    try {
      setLoading(true);
      const success = await mapService.removeNoiseLocation(id);
      if (success) {
        await loadNoiseLocations(); // Reload data after successful deletion
        await loadNoiseClusters(); // Sinkronkan layer cluster
      } else {
        setError("Gagal menghapus area berisik");
      }
    } catch (error: any) {
      logger.error("Error deleting noise location:", error);
      // Tampilkan pesan error dari server jika ada
      setError(error.message || "Gagal menghapus area berisik");
    } finally {
      setLoading(false);
    }
  };

  // DIPERBAHARUI: Handle locate user dengan icon
  const handleLocateUser = async () => {
    setLoading(true);
    setError(""); // Clear error sebelumnya
    try {
      const position = await mapService.getCurrentLocation();
      if (position) {
        setUserLocation(position);
        setIsTrackingUser(true); // Mulai tracking lokasi real-time
        // PERBAIKAN: Gunakan smooth zoom untuk lokasi user
        zoomToLocation(position, 16, false); // false karena sudah ada user location icon
      }
    } catch (error) {
      logger.error("Error getting location:", error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("Gagal mendapatkan lokasi");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearAreas = async () => {
    showConfirm(
      "Hapus Semua Area",
      "Apakah Anda yakin ingin menghapus semua area berisik milik Anda?",
      async () => {
        try {
          setLoading(true);
          const success = await mapService.clearAllNoiseLocations();
          if (success) {
            await loadNoiseLocations();
            await loadNoiseClusters();
            showSuccess("Berhasil!", "Semua area berisik telah dihapus.");
          } else {
            showError("Gagal", "Gagal menghapus area berisik");
          }
        } catch (error) {
          logger.error("Error clearing noise areas:", error);
          showError("Error", "Gagal menghapus area berisik");
        } finally {
          setLoading(false);
        }
      },
      undefined, // onCancel - default behavior
      "Ya, Hapus",
      "Batal"
    );
  };

  // NEW: Hapus seluruh laporan milik saya di cluster ini saja
  const handleDeleteClusterReports = async (cluster: NoiseCluster) => {
    if (!appConfig.backendEnabled) {
      showError(
        "Backend nonaktif",
        "Fitur hapus laporan dalam cluster hanya tersedia saat backend aktif."
      );
      return;
    }
    if (!isAuthenticated) {
      showLogin(
        "Login Diperlukan",
        "Anda harus login untuk menghapus laporan Anda dalam cluster.",
        () => navigate("/login")
      );
      return;
    }

    showConfirm(
      "Hapus Laporan Saya di Cluster",
      "Tindakan ini akan menghapus semua laporan yang Anda buat di cluster ini saja dan tidak menyentuh laporan pengguna lain. Lanjutkan?",
      async () => {
        try {
          setLoading(true);
          const deletedCount = await mapService.removeUserReportsInCluster(
            cluster
          );
          if (deletedCount > 0) {
            await loadNoiseLocations();
            await loadNoiseClusters();
            showSuccess(
              "Berhasil",
              `Terhapus ${deletedCount} laporan milik Anda dari cluster.`
            );
          } else {
            showError(
              "Tidak ada yang dihapus",
              "Tidak ditemukan laporan Anda di cluster ini atau terjadi kegagalan."
            );
          }
        } catch (error) {
          logger.error("Error deleting user reports in cluster:", error);
          showError("Gagal", "Gagal menghapus laporan Anda dalam cluster.");
        } finally {
          setLoading(false);
        }
      },
      undefined,
      "Ya, Hapus",
      "Batal"
    );
  };

  // ENHANCED: Improved search result click handler
  const handleSearchResultClick = (result: SearchResult) => {
    // PERBAIKAN: Set marker untuk lokasi pencarian dengan null check untuk address
    setSearchLocationMarker({
      position: result.coordinates,
      name: result.name,
      address: result.address || "Alamat tidak tersedia", // Fix untuk type error
    });

    // Smooth zoom ke hasil pencarian dengan zoom level yang lebih tinggi
    zoomToLocation(result.coordinates, 17, false); // false karena kita gunakan marker khusus

    // Clear search UI
    setSearchQuery("");
    setSearchResults([]);

    // Auto-hide marker setelah 10 detik
    setTimeout(() => {
      setSearchLocationMarker(null);
    }, 10000);
  };

  // BARU: Function untuk menghapus search marker secara manual
  const handleClearSearchMarker = () => {
    setSearchLocationMarker(null);
  };

  // Tutorial handlers
  const handleTutorialComplete = () => {
    setShowTutorial(false);
    localStorage.setItem("hasSeenMapTutorial", "true");
    showSuccess("Tutorial Selesai!", "Selamat menjelajahi peta kebisingan 🎉");
  };

  const handleTutorialSkip = () => {
    setShowTutorial(false);
    localStorage.setItem("hasSeenMapTutorial", "true");
  };

  const handleShowTutorial = () => {
    setShowTutorial(true);
  };

  // PERBAIKAN: Fungsi untuk handle keyboard navigation pada search results (dipulihkan)
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setSearchQuery("");
      setSearchResults([]);
      setSearchLocationMarker(null); // Clear search marker juga
    } else if (e.key === "Enter" && searchResults.length > 0) {
      // Auto select first result saat Enter
      handleSearchResultClick(searchResults[0]);
    } else if (e.key === "ArrowDown" && searchResults.length > 0) {
      // Future: implement keyboard navigation through results
      e.preventDefault();
    }
  };

  // Helpers for cluster styling and popup
  const categoryColors: Record<string, string> = {
    Traffic: "#ef4444", // merah
    Construction: "#f59e0b", // oranye
    Industry: "#8b5cf6", // ungu
    Event: "#10b981", // hijau tosca
    Nature: "#22c55e", // hijau
    Other: "#3b82f6", // biru
  };

  const getClusterColor = (cluster: NoiseCluster): string => {
    // Status expired = abu-abu
    if ((cluster.areaStatus || "") === "expired") return "#9aa0a6";
    // Gunakan kategori jika ada
    if (cluster.finalCategory && categoryColors[cluster.finalCategory]) {
      return categoryColors[cluster.finalCategory];
    }
    // Fallback ke warna berdasarkan rata-rata level kebisingan
    if (typeof cluster.noiseLevelAvg === "number") {
      return getNoiseColor(cluster.noiseLevelAvg);
    }
    return "#3b82f6"; // default biru
  };

  const formatDateTime = (d?: Date | null): string => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString();
    } catch {
      return "—";
    }
  };

  const getClusterIcon = (cluster: NoiseCluster) => {
    const color = getClusterColor(cluster);
    const status = resolveClusterAreaStatus(cluster);
    const ringColor = status === "expiring" ? color : "rgba(0,0,0,0.15)";
    const shadow =
      status === "expiring"
        ? `0 0 0 6px ${ringColor}55`
        : "0 2px 8px rgba(0,0,0,0.2)";

    const size = 38; // diameter
    const border =
      status === "expired" ? "2px dashed #9aa0a6" : `3px solid white`;

    const html = `
      <div style="
        display:flex;align-items:center;justify-content:center;
        width:${size}px;height:${size}px;border-radius:50%;
        background:${color};color:#fff;font-weight:700;font-size:14px;
        border:${border}; box-shadow:${shadow};
      ">
        ${Math.max(1, cluster.reportCount || 0)}
      </div>
    `;

    return L.divIcon({
      html,
      className: "noise-cluster-marker",
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  };

  // ENHANCED: Component for handling map events
  const MapEvents: React.FC = () => {
    useMapEvents({
      click: (e) => {
        handleMapClick(e);
      },
      zoomstart: () => {
        // Optional: behavior saat zoom dimulai
      },
    });
    return null;
  };
  const getClusterCategoryDisplay = (cluster: NoiseCluster): string => {
    const rawFinal = (cluster.finalCategory || "").trim();
    // Jika finalCategory bukan Mixed, tampilkan apa adanya
    if (rawFinal && rawFinal.toLowerCase() !== "mixed") return rawFinal;

    // Ketika Mixed atau kosong: turunkan daftar kategori dari noiseSources
    const sourceCats: string[] = Array.isArray(cluster.noiseSources)
      ? Array.from(
          new Set(cluster.noiseSources.map((s) => deriveFinalCategory(s)))
        )
      : [];

    // Terapkan filter kategori (jika user memilih)
    const selectedCats = activeFilters.category?.length
      ? sourceCats.filter((c) => activeFilters.category!.includes(c))
      : sourceCats;

    // Jika hasil filter mengerucut ke satu kategori, tampilkan kategori itu saja
    if (selectedCats.length === 1) return selectedCats[0];

    // Jika hasil filter memuat beberapa kategori, tampilkan sebagai daftar (bukan sekadar "Mixed")
    if (selectedCats.length > 1) return selectedCats.join(", ");

    // Jika filter menyingkirkan semua kategori, tampilkan semua kategori yang ada (jika ada)
    if (sourceCats.length > 0) return sourceCats.join(", ");

    // Fallback terakhir
    return rawFinal || "Mixed";
  };

  // BARU: Tampilkan label sumber yang menyesuaikan filter kategori aktif
  const getClusterSourcesDisplay = (cluster: NoiseCluster): string => {
    // Kumpulkan sumber unik dari cluster
    const sources: string[] = Array.isArray(cluster.noiseSources)
      ? Array.from(new Set(cluster.noiseSources))
      : [];

    if (sources.length === 0) return "—";

    // Jika ada filter kategori aktif, saring sumber berdasarkan kategori yang diturunkan
    const activeCats = activeFilters.category || [];
    const filteredSources = activeCats.length
      ? sources.filter((s) => activeCats.includes(deriveFinalCategory(s)))
      : sources;

    // Jika setelah filter tidak ada yang tersisa, tampilkan semua sumber agar user tahu asalnya
    const displaySources = filteredSources.length ? filteredSources : sources;

    // Tampilkan dengan terjemahan yang user-friendly
    return displaySources.map((s) => translateNoiseSource(s)).join(", ");
  };

  const filteredNoiseLocations = useMemo(() => {
    const { noiseLevel, category, healthImpact } = activeFilters;

    if (!noiseLevel?.length && !category?.length && !healthImpact?.length) {
      return noiseLocations;
    }

    // Fungsi helper untuk konversi noise level
    const getNoiseLevelCategory = (level: number): string => {
      if (level === 0) return "Sedang dalam perbaikan";
      if (level <= 40) return "Tenang";
      if (level <= 60) return "Sedang";
      if (level <= 80) return "Berisik";
      return "Sangat Berisik";
    };

    return noiseLocations.filter((location) => {
      const locationNoiseLevelCategory = getNoiseLevelCategory(
        location.noiseLevel
      );
      const noiseLevelMatch =
        !noiseLevel?.length || noiseLevel.includes(locationNoiseLevelCategory);

      // Tentukan kategori final untuk lokasi
      const locationCategory =
        location.final_category || deriveFinalCategory(location.source);
      const categoryMatch =
        !category?.length || category.includes(locationCategory);

      const healthImpactMatch =
        !healthImpact?.length || healthImpact.includes(location.healthImpact);
      return noiseLevelMatch && categoryMatch && healthImpactMatch;
    });
  }, [noiseLocations, activeFilters]);

  return (
    <div className={`${styles.mapContainer} ${className || ""}`}>
      {/* Search Container and MapControls remain the same */}
      <input
        type="file"
        accept="audio/*"
        style={{ display: "none" }}
        ref={reanalysisFileInputRef}
        onChange={handleReanalysisFileSelected}
      />
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Cari lokasi..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          className={`${styles.searchInput} tutorial-search-input`}
          id="search-location-input"
        />

        {searchResults.length > 0 && (
          <div className={styles.searchResults}>
            {searchResults.map((result, index) => (
              <div
                key={result.id}
                className={`${styles.searchResult} ${
                  index === 0 ? styles.firstResult : ""
                }`}
                onClick={() => handleSearchResultClick(result)}
              >
                <div className={styles.resultName}>{result.name}</div>
                <div className={styles.resultAddress}>
                  {result.address || "Alamat tidak tersedia"}
                </div>
              </div>
            ))}
          </div>
        )}

        {isSearching && (
          <div className={styles.searchResult}>
            <div className={styles.resultName}>🔍 Mencari...</div>
          </div>
        )}

        {/* PERBAIKAN: Show message when no results found */}
        {!isSearching &&
          searchQuery.length > 2 &&
          searchResults.length === 0 && (
            <div className={styles.searchResult}>
              <div className={styles.resultName}>Tidak ada hasil ditemukan</div>
            </div>
          )}
      </div>
      <MapControls
        onAddNoiseArea={handleAddNoiseArea}
        onLocateUser={handleLocateUser}
        onClearAreas={handleClearAreas}
        onToggleLegend={() => setShowLegend(!showLegend)}
        onToggleFilter={() => setShowFilter(!showFilter)}
        onToggleTracking={() => setIsTrackingUser(!isTrackingUser)}
        onShowTutorial={handleShowTutorial}
        onToggleChatbot={() => setIsChatOpen(!isChatOpen)}
        isChatOpen={isChatOpen}
        isAddingNoise={isAddingNoise}
        showLegend={showLegend}
        showFilter={showFilter}
        isTrackingUser={isTrackingUser}
        hasUserLocation={userLocation !== null}
        backendDisabled={!appConfig.backendEnabled}
      />

      {/* ChatBot Window - rendered via Portal */}
      <ChatBot
        clusters={noiseClusters}
        isOpen={isChatOpen}
        onToggle={() => setIsChatOpen(!isChatOpen)}
        onFlyTo={(target) => {
          if (mapRef.current && target.coordinates) {
            mapRef.current.flyTo(target.coordinates, 16, {
              animate: true,
              duration: 2,
              easeLinearity: 0.25,
            });
          }
        }}
      />
      {showFilter && (
        <div className="tutorial-filter-container" id="filter-container">
          <AreaFilter
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            noiseLocations={noiseLocations}
          />
        </div>
      )}

      {/* BARU: User location marker */}
      {/* UPDATED: Noise Panel is now the form for the new flow */}
      {showNoiseForm && selectedPosition && (
        <div className={styles.noisePanel}>
          {!appConfig.backendEnabled && (
            <div
              style={{
                background: "#fbbf24",
                color: "#92400e",
                padding: "12px",
                borderRadius: "8px",
                marginBottom: "16px",
                border: "1px solid #f59e0b",
              }}
            >
              ⚠️ Fitur analisis audio dinonaktifkan sementara
            </div>
          )}
          <div className={styles.noisePanelTitle}>
            Analisis Kebisingan di Lokasi
          </div>
          <div className={styles.noiseForm}>
            <div className={styles.noiseLevel}>
              <span>Koordinat:</span>
              <span className={styles.noiseLevelValue}>
                {selectedPosition[0].toFixed(5)},{" "}
                {selectedPosition[1].toFixed(5)}
              </span>
            </div>
            <input
              type="text"
              placeholder="Alamat/Nama Lokasi (opsional)"
              value={formAddress}
              onChange={(e) => setFormAddress(e.target.value)}
              className={styles.noiseInput}
              disabled={!appConfig.backendEnabled}
            />
            <input
              type="text"
              placeholder="Deskripsi (misal: 'suara dari proyek')"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              className={styles.noiseInput}
              disabled={!appConfig.backendEnabled}
            />
            <div className={styles.fileInputContainer}>
              <label htmlFor="audio-upload" className={styles.fileInputLabel}>
                Pilih File Audio
              </label>
              <input
                id="audio-upload"
                type="file"
                accept="audio/*"
                onChange={handleFileChange} // <--- Handler baru
                className={styles.fileInput}
                disabled={isUploading || !appConfig.backendEnabled}
              />
              {audioFile && (
                <span className={styles.fileName}>{audioFile.name}</span>
              )}
            </div>
            <button
              onClick={handleUploadAndAnalyze} // <--- Handler baru
              className={styles.submitButton}
              disabled={isUploading || !audioFile || !appConfig.backendEnabled}
            >
              {!appConfig.backendEnabled
                ? "Fitur Dinonaktifkan"
                : isUploading
                ? "Menganalisis..."
                : "Upload & Analisis"}
            </button>
          </div>
        </div>
      )}

      {/* Legend, Loading, Error, and MapContainer JSX remain the same */}
      {showLegend && (
        <div
          className={`${styles.legend} tutorial-legend-container`}
          id="legend-container"
        >
          <div className={styles.legendTitle}>Legenda</div>
          {Object.entries(noiseColors).map(([key, color]) => {
            let label = "";
            switch (key) {
              case "low":
                label = "Tenang (≤40 dB)";
                break;
              case "medium":
                label = "Sedang (41-60 dB)";
                break;
              case "high":
                label = "Berisik (61-80 dB)";
                break;
              case "extreme":
                label = "Sangat Berisik (>80 dB)";
                break;
            }
            return (
              <div className={styles.legendItem} key={key}>
                <div
                  className={styles.legendColor}
                  style={{ backgroundColor: color }}
                />
                <span>{label}</span>
              </div>
            );
          })}
          {/* BARU: Tambahkan legenda untuk lokasi pengguna */}
          <div className={styles.legendItem}>
            <div
              className={styles.legendColor}
              style={{
                backgroundColor: "#007AFF",
                borderRadius: "50%",
                border: "2px solid white",
                boxShadow: "0 2px 4px rgba(0,122,255,0.3)",
              }}
            />
            <span>Lokasi Anda</span>
          </div>
          {/* BARU: Tambahkan legenda untuk lokasi pencarian */}
          <div className={styles.legendItem}>
            <div
              className={styles.legendColor}
              style={{
                backgroundColor: "#ff4444",
                borderRadius: "50%",
                border: "2px solid white",
                boxShadow: "0 2px 4px rgba(255,68,68,0.3)",
              }}
            />
            <span>Lokasi Pencarian</span>
          </div>
        </div>
      )}
      {loading && (
        <div className={styles.loading}>
          <div>📍 Memuat Lokasi...</div>
        </div>
      )}
      {error && (
        <div className={styles.errorMessage}>
          {error}
          <button
            onClick={() => setError("")}
            style={{
              marginLeft: "10px",
              background: "none",
              border: "none",
              color: "white",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}
      <MapContainer
        center={mapConfig.center}
        zoom={mapConfig.zoom}
        className={styles.mapInstance}
        ref={mapRef}
        attributionControl={mapConfig.attributionControl}
        zoomControl={mapConfig.zoomControl}
      >
        <TileLayer
          url={tileLayerConfig.url}
          attribution={tileLayerConfig.attribution}
        />

        <MapEvents />

        {/* Noise Areas as Circles */}
        {filteredNoiseLocations.map((location) => {
          const area = generateNoiseArea(location);
          const status =
            location.status ||
            computeNoiseAreaStatus(location.timestamp, location.expires_at);
          const style = getCircleStyleByStatus(
            status,
            area.color,
            area.opacity
          );
          const tooltipText = getStatusTooltip(status);
          const isCovered = isLocationCoveredByAnyCluster(location);
          // Jika lokasi sudah tergabung dalam cluster, jangan render lingkaran individual
          if (isCovered) return null;
          return (
            <Circle
              key={location.id}
              center={area.center}
              radius={area.radius}
              pathOptions={style}
              interactive={!isAddingNoise}
            >
              {!isAddingNoise && tooltipText && (
                <Tooltip
                  direction="top"
                  offset={[0, -8]}
                  opacity={1}
                  permanent={false}
                >
                  <span style={{ fontSize: 12 }}>{tooltipText}</span>
                </Tooltip>
              )}
              {/* Popup single-report tetap untuk lokasi non-cluster */}
              {!isAddingNoise && (
                <Popup>
                  <MapPopup
                    location={location}
                    onDelete={handleDeleteNoiseLocation}
                    onReanalyze={handleStartReanalysis}
                    currentUserId={localStorage.getItem("userId")}
                  />
                </Popup>
              )}
            </Circle>
          );
        })}

        {/* BARU: User Location Marker */}
        {userLocation && (
          <Marker
            position={userLocation}
            icon={userLocationIcon}
            interactive={!isAddingNoise}
          >
            {!isAddingNoise && (
              <Popup>
                <div style={{ textAlign: "center", padding: "8px" }}>
                  <strong>📍 Lokasi Anda</strong>
                  <br />
                  <small>
                    {userLocation[0].toFixed(6)}, {userLocation[1].toFixed(6)}
                  </small>
                  <br />
                  <small style={{ color: "#666" }}>
                    {isTrackingUser ? "🔄 Tracking aktif" : "📌 Lokasi tetap"}
                  </small>
                </div>
              </Popup>
            )}
          </Marker>
        )}

        {/* BARU: Search Location Marker */}
        {searchLocationMarker && (
          <Marker
            position={searchLocationMarker.position}
            icon={searchLocationIcon}
            interactive={!isAddingNoise}
          >
            {!isAddingNoise && (
              <Popup>
                <div
                  style={{
                    textAlign: "center",
                    padding: "10px",
                    minWidth: "200px",
                  }}
                >
                  <strong style={{ fontSize: "16px", color: "#ff4444" }}>
                    📍 {searchLocationMarker.name}
                  </strong>
                  <br />
                  <div
                    style={{ margin: "8px 0", color: "#666", fontSize: "14px" }}
                  >
                    {searchLocationMarker.address}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#999",
                      marginBottom: "10px",
                    }}
                  >
                    {searchLocationMarker.position[0].toFixed(6)},{" "}
                    {searchLocationMarker.position[1].toFixed(6)}
                  </div>
                  <button
                    onClick={handleClearSearchMarker}
                    style={{
                      background: "#ff4444",
                      color: "white",
                      border: "none",
                      padding: "6px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    Tutup Marker
                  </button>
                </div>
              </Popup>
            )}
          </Marker>
        )}

        {/* Selected Position Marker */}
        {selectedPosition && <Marker position={selectedPosition} />}

        {/* PERBAIKAN: Search Result Marker (legacy - untuk fallback) */}
        {searchMarker && (
          <Marker
            position={searchMarker}
            icon={L.divIcon({
              className: "search-marker",
              html: '<div style="background: #ff4444; border: 2px solid white; border-radius: 50%; width: 20px; height: 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
              iconSize: [20, 20],
              iconAnchor: [10, 10],
            })}
            interactive={!isAddingNoise}
          />
        )}

        {/* NEW: Cluster markers layer */}
        {noiseClusters
          .filter((cluster) => (cluster.reportCount ?? 0) > 1)
          .map((cluster) => {
            const areaStatus = resolveClusterAreaStatus(cluster);
            const avg =
              typeof cluster.noiseLevelAvg === "number"
                ? cluster.noiseLevelAvg
                : 50;
            const radius = getNoiseRadius(avg);
            const baseColor = getNoiseColor(avg);
            const baseOpacity = getNoiseOpacity(avg);
            const style = getCircleStyleByStatus(
              areaStatus,
              baseColor,
              baseOpacity
            );
            const [lat, lon] = cluster.center;
            const icon = getClusterIcon(cluster);
            return (
              <React.Fragment key={`cluster-wrap-${cluster.id}`}>
                <Circle
                  key={`cluster-area-${cluster.id}`}
                  center={[lat, lon]}
                  radius={radius}
                  pathOptions={style}
                  interactive={false}
                >
                  {/* Popup dihapus agar tidak muncul dua kali; interaksi cluster hanya melalui Marker */}
                </Circle>
                <Marker
                  key={`cluster-${cluster.id}`}
                  position={[lat, lon]}
                  icon={icon}
                  interactive={!isAddingNoise}
                >
                  {!isAddingNoise && (
                    <Popup>
                      <div style={{ minWidth: 220 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>
                          LAPORAN KEBISINGAN
                        </div>
                        <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                          <div>
                            <strong>Status:</strong> {String(areaStatus)}
                          </div>
                          <div>
                            <strong>Status Area:</strong> {getNoiseDescription(avg)}
                          </div>
                          <div>
                            <strong>Jumlah Laporan:</strong>{" "}
                            {cluster.reportCount}
                          </div>
                          {typeof cluster.noiseLevelAvg === "number" && (
                            <div>
                              <strong>Rata-rata:</strong>{" "}
                              {formatNoiseLevel(cluster.noiseLevelAvg)}
                            </div>
                          )}
                          {(cluster.finalCategory ||
                            (Array.isArray(cluster.noiseSources) &&
                              cluster.noiseSources.length > 0)) && (
                            <div>
                              <strong>Kategori Dominan:</strong>{" "}
                              {getClusterCategoryDisplay(cluster)}
                            </div>
                          )}
                          {Array.isArray(cluster.noiseSources) &&
                            cluster.noiseSources.length > 0 && (
                              <div>
                                <strong>Sumber:</strong>{" "}
                                {getClusterSourcesDisplay(cluster)}
                              </div>
                            )}
                          {typeof cluster.noiseLevelAvg === "number" && (
                            <div>
                              <strong>Dampak Kesehatan:</strong>{" "}
                              {translateHealthImpact(
                                cluster.noiseLevelAvg < 55
                                  ? "Aman"
                                  : cluster.noiseLevelAvg < 70
                                  ? "Perhatian"
                                  : cluster.noiseLevelAvg < 85
                                  ? "Berbahaya"
                                  : "Sangat Berbahaya"
                              )}
                            </div>
                          )}
                          <div>
                            <strong>Koordinat:</strong> (
                            {formatCoordinates(lat, lon)})
                          </div>
                          <div>
                            <strong>Radius:</strong> {formatRadius(radius)}
                          </div>
                          {clusterAddresses[cluster.id] && (
                            <div>
                              <strong>Alamat:</strong>{" "}
                              {clusterAddresses[cluster.id]}
                            </div>
                          )}
                          <div>
                            <strong>Periode:</strong>{" "}
                            {formatDateTime(cluster.firstCreatedAt)} →{" "}
                            {formatDateTime(cluster.lastCreatedAt)}
                          </div>
                          {cluster.addedByUsernames?.length > 0 && (
                            <div>
                              <strong>Kontributor:</strong>{" "}
                              {cluster.addedByUsernames.slice(0, 3).join(", ")}
                              {cluster.addedByUsernames.length > 3
                                ? ", ..."
                                : ""}
                            </div>
                          )}
                          {isAuthenticated &&
                            hasCurrentUserContribution(cluster) && (
                              <div style={{ marginTop: 10 }}>
                                <button
                                  onClick={() =>
                                    handleDeleteClusterReports(cluster)
                                  }
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "6px 10px",
                                    borderRadius: 6,
                                    border: "1px solid #e11d48",
                                    background: "#fee2e2",
                                    color: "#b91c1c",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  🗑️ Hapus Laporan Saya
                                </button>
                              </div>
                            )}
                        </div>
                      </div>
                    </Popup>
                  )}
                </Marker>
              </React.Fragment>
            );
          })}
      </MapContainer>
      <PopupComponent />

      {/* Tutorial Component */}
      <MapTutorial
        isVisible={showTutorial}
        onComplete={handleTutorialComplete}
        onSkip={handleTutorialSkip}
      />

    </div>
  );
};

export default MapComponent;

// Helper: resolve area status from DB or compute fallback
const VALID_AREA_STATUSES: NoiseAreaStatus[] = ["active", "expiring", "expired", "permanent"];
const resolveClusterAreaStatus = (cluster: NoiseCluster): NoiseAreaStatus => {
  const raw = (cluster.areaStatus ?? "").toString().toLowerCase().trim();
  if ((VALID_AREA_STATUSES as string[]).includes(raw)) {
    return raw as NoiseAreaStatus;
  }
  const created = cluster.firstCreatedAt || new Date();
  const expires = cluster.maxExpiresAt || undefined;
  return computeNoiseAreaStatus(created, expires);
};
